import * as user from '../actions/user';
import * as settings from '../actions/settings';
import { UserLocation } from '../models/user/location.model';
import moment from 'moment-timezone';
import { AppConstants } from '../app.constants';
import { DiscoverConstants } from '../discover/discover.constants';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { isInMaintenance, MaintenanceWindow } from '../models/shared/maintenance-window';

export interface State {
  adminFull?: boolean;
  adminView?: boolean;
  alertDialog?: any;
  email?: string;
  error?: string;
  firstName?: string;
  guid?: string;
  featureFlags: LDFlagSet;
  lastName?: string;
  loading: boolean;
  locationError?: string;
  locations: any[];
  selectedLocation: UserLocation | null;
  userName?: string;
  userSettings: any;
  welcomeModal: boolean;
}

export const initialState: State = {
  featureFlags: {},
  loading: true,
  locations: [],
  selectedLocation: null,
  // favoriteLocations is intentionally omitted (rather than defaulted to []) until a load
  // actually succeeds - an unfetched/failed load must stay distinguishable from a confirmed
  // empty list, since settings.service.ts serializes [] into a real "clear favorites" write.
  userSettings: { darkModeEnabled: false },
  welcomeModal: false
};

export function reducer(state = initialState, action: user.Actions | settings.Actions): State {
  switch (action.type) {
    case settings.ActionTypes.LOAD_SETTINGS_SUCCESS:
    case settings.ActionTypes.SAVE_FAVORITE_LOCATIONS:
    case settings.ActionTypes.FAVORITE_LOCATIONS_UPDATED: {
      const userSettings = {
        ...state.userSettings,
        ...action.payload
      };
      return { ...state, userSettings };
    }

    case user.ActionTypes.DISMISS_BANNER_SUCCESS: {
      let banner = action.payload;
      if (banner === DiscoverConstants.welcomeModalBanner) {
        return { ...state, welcomeModal: false }
      }
      return state;
    }

    case user.ActionTypes.GET_LOCATION_DETAILS_FAILURE: {
      let locationError = action.payload;
      return { ...state, loading: false, locationError };
    }

    case user.ActionTypes.GET_LOCATION_HIGH_ALERT_DETAILS_SUCCESS: {
      let highMessagingAlert: boolean = action.payload;
      return {
        ...state,
        selectedLocation: {
          ...state.selectedLocation,
          highMessagingAlert
        }
      };
    }

    case user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS: {
      const selectedLocation = action.payload;
      let cfaDelivery = true;
      let doorDash = true;
      let featureFlags = state.featureFlags;
      let pickup = true;

      if (selectedLocation && selectedLocation.timeZone) {
        moment.tz.setDefault(selectedLocation.timeZone);
      }

      // Turn off the FIC tile if all 3 FIC destination option flags are false
      if (featureFlags && featureFlags.freeItemInCartCFADelivery) {
        cfaDelivery = featureFlags.freeItemInCartCFADelivery.enabledForAllLocations ||
          (featureFlags.freeItemInCartCFADelivery.enabledForLocations &&
            featureFlags.freeItemInCartCFADelivery.enabledForLocations.includes(
              selectedLocation.locationNumber
            )
          );
      }
      if (featureFlags && featureFlags.freeItemInCartDoorDash) {
        doorDash = featureFlags.freeItemInCartDoorDash.enabledForAllLocations || (featureFlags.freeItemInCartDoorDash.enabledForLocations
          && featureFlags.freeItemInCartDoorDash.enabledForLocations.includes(selectedLocation.locationNumber));
      }
      if (featureFlags && featureFlags.freeItemInCartPickup) {
        pickup = featureFlags.freeItemInCartPickup.enabledForAllLocations || (featureFlags.freeItemInCartPickup.enabledForLocations
          && featureFlags.freeItemInCartPickup.enabledForLocations.includes(selectedLocation.locationNumber));
      }

      if (!pickup && !cfaDelivery && !doorDash) {
        const index = featureFlags.freeItemInCartEnabled.enabledForLocations.indexOf(selectedLocation.locationNumber);
        featureFlags.freeItemInCartEnabled.enabledForLocations.splice(index, 1);
      }

      return {
        ...state,
        featureFlags: featureFlags,
        loading: false,
        locationError: undefined,
        selectedLocation: {
          ...selectedLocation,
          highMessagingAlert: state.selectedLocation?.highMessagingAlert ? state.selectedLocation.highMessagingAlert : false
        }
      };
    }

    case user.ActionTypes.FEATURE_FLAGS_SET: {
      let featureFlags = action.payload;
      const maintenanceWindow: MaintenanceWindow = featureFlags[AppConstants.maintenanceWindow];

      if (isInMaintenance(maintenanceWindow)) {
        // If it is during the maintenance window then set all the tab disable flags to true, aside from recover
        featureFlags = {
          ...featureFlags,
          disableCalendarTab: { 'enabledForAllLocations': true, 'enabledForLocations': [] },
          disableDiscoverTab: { 'enabledForAllLocations': true, 'enabledForLocations': [] },
          disableEngageTab: { 'enabledForAllLocations': true, 'enabledForLocations': [] },
          disableMeetTab: { 'enabledForAllLocations': true, 'enabledForLocations': [] },
          disableUnderstandTab: { 'enabledForAllLocations': true, 'enabledForLocations': [] }
        }
      }

      return { ...state, featureFlags: featureFlags }
    }

    case user.ActionTypes.SELECT_LOCATION: {
      const newLocation = action.payload;
      // For admin users the location payload will be mostly empty data
      const selectedLocation = (state.adminFull || state.adminView) ? state.selectedLocation : newLocation;

      if (selectedLocation && selectedLocation.locationTimeZone) {
        moment.tz.setDefault(selectedLocation.timeZone);
      }

      return {
        ...state,
        loading: true,
        locationError: undefined,
        selectedLocation: {
          ...selectedLocation,
          ...newLocation
        }
      };
    }

    case user.ActionTypes.USER_DATA_ERROR: {
      let error = action.payload;

      return {
        ...state,
        error,
        loading: false
      };
    }

    case user.ActionTypes.USER_DATA_FOUND: {
      const userData = action.payload;
      let selectedLocation = userData.locations[0];
      let welcomeModal = false;
      const userSettings = {
        ...initialState.userSettings,
        ...state.userSettings,
        ...userData.userSettings
      };

      if (selectedLocation.notificationBanners) {
        selectedLocation.notificationBanners.map((banner: any) => {
          if (banner.id === DiscoverConstants.welcomeModalBanner) {
            welcomeModal = true;
          }
        });
      }

      return {
        ...state,
        ...userData,
        userSettings,
        loading: false,
        selectedLocation,
        welcomeModal
      };
    }

    default: {
      return state;
    }
  }
}

export const getAdminFull = (state: State) => state.adminFull;

export const getAdminView = (state: State) => state.adminView;

export const getAlertDialog = (state: State) => state.alertDialog;

export const getFavoriteLocations = (state: State) => state.userSettings.favoriteLocations ?? [];

export const getDarkModeEnabled = (state: State) => !!state.userSettings?.darkModeEnabled;

export const getError = (state: State) => state.error;

export const getFeatureFlags = (state: State) => state.featureFlags;

export const getLoading = (state: State) => state.loading;

export const getLocationError = (state: State) => state.locationError;

export const getLocations = (state: State) => state.locations;

export const getSelectedLocation = (state: State) => state.selectedLocation;

export const getUserName = (state: State) => state.userName;

export const getWelcomeModal = (state: State) => state.welcomeModal;
